import React, { useState, useEffect, useRef } from 'react';
import { ROOMS, ROOM_TYPES, RESERVATION_LIMITS, RECURRING_PATTERNS, CALENDAR_SETTINGS } from '../../utils/constants';
import { formatDate, formatTime, formatDateTime, roundToQuarterHour, addMinutesToTime, roundToHour, generateTimeOptions30Min } from '../../utils/timeUtils';
import { useAuth } from '../../context/AuthContext';
import { useCompanies } from '../../hooks/useCompanies';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import RecurringPatternEditor from './RecurringPatternEditor';
import '../../styles/modal.css';

/**
 * 予約作成・編集モーダル
 */
const ReservationModal = ({
  isOpen,
  onClose,
  reservation,
  initialData,
  onSave,
  onDelete,
}) => {
  const isEditing = !!reservation;
  const { isSystemAdmin, isCompanyAdminOrAbove, getCompanyId, canEditReservation, canDeleteReservation } = useAuth();
  const { companies, loading: companiesLoading } = useCompanies();

  // 編集権限チェック
  const canEdit = isEditing ? canEditReservation(reservation) : true;
  const canDelete = isEditing ? canDeleteReservation(reservation) : false;
  const isReadOnly = isEditing && !canEdit;

  // フォームの状態
  const [formData, setFormData] = useState({
    room_type: '',
    title: '',
    start_datetime: new Date(),
    end_datetime: addMinutesToTime(new Date(), 60),
    notes: '',
    is_recurring: false,
    recurring_pattern: null,
    company_id: getCompanyId() || null, // デフォルトは自社
  });

  const [errors, setErrors] = useState({});
  const [showRecurringOptions, setShowRecurringOptions] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);

  // 30分単位の時間オプションを生成
  const timeOptions = generateTimeOptions30Min();

  // 時間ドロップダウンの表示状態
  const [showStartDropdown, setShowStartDropdown] = useState(false);
  const [showEndDropdown, setShowEndDropdown] = useState(false);

  // ドロップダウンの外側クリック検知用Ref
  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);

  // アクセシビリティ: フォーカストラップとEscキー対応
  const modalRef = useFocusTrap(isOpen);
  useEscapeKey(onClose, isOpen);

  // 外側クリックでドロップダウンを閉じる
  useEffect(() => {
    const handleDocumentMouseDown = (event) => {
      if (showStartDropdown && startTimeRef.current && !startTimeRef.current.contains(event.target)) {
        setShowStartDropdown(false);
      }
      if (showEndDropdown && endTimeRef.current && !endTimeRef.current.contains(event.target)) {
        setShowEndDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [showStartDropdown, showEndDropdown]);

  // 初期データの設定
  useEffect(() => {
    if (reservation) {
      // 既存予約の編集
      const start = new Date(reservation.start);
      const end = new Date(reservation.end);

      setFormData({
        room_type: reservation.room_type,
        title: reservation.title,
        start_datetime: start,
        end_datetime: end,
        notes: reservation.notes || '',
        is_recurring: reservation.is_recurring || false,
        recurring_pattern: reservation.recurring_pattern || null,
        company_id: reservation.company_id || getCompanyId(),
      });

      // 繰り返し予約の場合はオプションを表示
      setShowRecurringOptions(reservation.is_recurring || false);
    } else if (initialData && (initialData.start_datetime || initialData.end_datetime)) {
      // 新規予約（初期データあり）でも開始・終了は1時間単位に丸める
      const baseStart = initialData.start_datetime ? new Date(initialData.start_datetime) : new Date();
      const roundedStart = roundToHour(baseStart);
      const roundedEnd = addMinutesToTime(roundedStart, 60);

      setFormData({
        ...formData,
        ...initialData,
        start_datetime: roundedStart,
        end_datetime: roundedEnd,
        company_id: initialData.company_id || getCompanyId(),
      });
    } else if (isOpen && !reservation) {
      // 通常の新規予約（initialDataなし、または時間データなし）
      const now = new Date();
      const roundedStart = roundToHour(now);
      const roundedEnd = addMinutesToTime(roundedStart, 60);

      setFormData({
        room_type: '',
        title: '',
        start_datetime: roundedStart,
        end_datetime: roundedEnd,
        notes: '',
        is_recurring: false,
        recurring_pattern: null,
        company_id: getCompanyId() || null,
      });
    }
  }, [reservation, initialData, isOpen]);

  // formData.is_recurringの変更を監視してshowRecurringOptionsを同期
  useEffect(() => {
    setShowRecurringOptions(formData.is_recurring);
  }, [formData.is_recurring]);

  /**
   * フォームフィールドの変更
   */
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    // サーバーエラーもクリア
    if (serverError) {
      setServerError(null);
    }
  };

  /**
   * 日付の変更
   */
  const handleDateChange = (dateValue) => {
    const date = new Date(dateValue);
    const startTime = new Date(formData.start_datetime);
    const endTime = new Date(formData.end_datetime);

    // 期間を保持（ミリ秒）
    const duration = endTime - startTime;

    // 新しい開始日時
    date.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
    const newStart = roundToQuarterHour(date);

    // 新しい終了日時（期間を保持）
    const newEnd = new Date(newStart.getTime() + duration);

    setFormData(prev => ({
      ...prev,
      start_datetime: newStart,
      end_datetime: newEnd
    }));
  };

  /**
   * 開始時間の変更
   */
  const handleStartTimeChange = (timeValue) => {
    const [hours, minutes] = timeValue.split(':').map(Number);
    const date = new Date(formData.start_datetime);
    const endTime = new Date(formData.end_datetime);

    // 期間を保持（ミリ秒）
    const duration = endTime - formData.start_datetime;

    // 新しい開始日時（ドロップダウン選択時は15分単位）
    date.setHours(hours, minutes, 0, 0);
    const newStart = roundToQuarterHour(date);

    // 新しい終了日時（期間を保持）
    const newEnd = new Date(newStart.getTime() + duration);

    setFormData(prev => ({
      ...prev,
      start_datetime: newStart,
      end_datetime: newEnd
    }));
  };

  /**
   * 開始時間の手入力（丸めなし）
   */
  const handleStartTimeInput = (timeValue) => {
    const [hours, minutes] = timeValue.split(':').map(Number);
    const date = new Date(formData.start_datetime);
    const endTime = new Date(formData.end_datetime);

    const duration = endTime - formData.start_datetime;

    date.setHours(hours, minutes, 0, 0);
    const newStart = date; // 丸めない

    const newEnd = new Date(newStart.getTime() + duration);

    setFormData(prev => ({
      ...prev,
      start_datetime: newStart,
      end_datetime: newEnd
    }));
  };

  /**
   * 開始時間のフォーカスアウト時に15分単位へ丸め
   */
  const handleStartTimeBlur = () => {
    const date = new Date(formData.start_datetime);
    const endTime = new Date(formData.end_datetime);
    const duration = endTime - formData.start_datetime;

    const newStart = roundToQuarterHour(date);
    const newEnd = new Date(newStart.getTime() + duration);

    setFormData(prev => ({
      ...prev,
      start_datetime: newStart,
      end_datetime: newEnd
    }));
  };

  /**
   * 終了時間の変更
   */
  const handleEndTimeChange = (timeValue) => {
    const [hours, minutes] = timeValue.split(':').map(Number);
    const date = new Date(formData.end_datetime);
    date.setHours(hours, minutes, 0, 0);

    const newEnd = roundToQuarterHour(date);

    setFormData(prev => ({
      ...prev,
      end_datetime: newEnd
    }));
  };

  /**
   * 終了時間の手入力（丸めなし）
   */
  const handleEndTimeInput = (timeValue) => {
    const [hours, minutes] = timeValue.split(':').map(Number);
    const date = new Date(formData.end_datetime);
    date.setHours(hours, minutes, 0, 0);

    setFormData(prev => ({
      ...prev,
      end_datetime: date
    }));
  };

  /**
   * 終了時間のフォーカスアウト時に15分単位へ丸め
   */
  const handleEndTimeBlur = () => {
    const date = new Date(formData.end_datetime);
    const newEnd = roundToQuarterHour(date);
    setFormData(prev => ({
      ...prev,
      end_datetime: newEnd
    }));
  };

  /**
   * バリデーション
   */
  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'タイトルを入力してください';
    }

    if (!formData.room_type) {
      newErrors.room_type = '会議室を選択してください';
    }

    if (isSystemAdmin() && !formData.company_id) {
      newErrors.company_id = '予約企業を選択してください';
    }

    // 期間のチェック（start_datetimeとend_datetimeの差分から計算）
    const durationMinutes = Math.round((formData.end_datetime - formData.start_datetime) / (1000 * 60));

    if (durationMinutes < 15) {
      newErrors.end_datetime = '予約時間は最低15分必要です';
    }

    const durationHours = durationMinutes / 60;
    if (durationHours > RESERVATION_LIMITS.MAX_DURATION_HOURS) {
      newErrors.end_datetime = `予約時間は最長${RESERVATION_LIMITS.MAX_DURATION_HOURS}時間までです`;
    }

    // 終了時間が開始時間より前の場合
    if (formData.end_datetime <= formData.start_datetime) {
      newErrors.end_datetime = '終了時間は開始時間より後である必要があります';
    }

    // 営業時間チェック (7:00-22:00)
    const { START_HOUR, END_HOUR } = CALENDAR_SETTINGS;
    const startHour = formData.start_datetime.getHours();
    const endHour = formData.end_datetime.getHours();
    const endMinute = formData.end_datetime.getMinutes();

    if (startHour < START_HOUR || startHour >= END_HOUR) {
      newErrors.start_datetime = `開始時刻は${START_HOUR}:00〜${END_HOUR}:00の間である必要があります`;
    }

    if (endHour > END_HOUR || (endHour === END_HOUR && endMinute > 0)) {
      newErrors.end_datetime = `終了時刻が営業時間（${END_HOUR}:00）を超えています`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * 保存処理
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // クライアント側のバリデーション
    if (!validate()) {
      return;
    }

    // サーバーエラーをクリア
    setServerError(null);

    // ローカル時刻をISO形式の文字列に変換（タイムゾーンオフセットなし）
    const formatLocalDateTime = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };

    const saveData = {
      room_type: formData.room_type,
      title: formData.title,
      start_datetime: formatLocalDateTime(formData.start_datetime),
      end_datetime: formatLocalDateTime(formData.end_datetime),
      notes: formData.notes,
      is_recurring: formData.is_recurring,
      recurring_pattern: formData.is_recurring ? formData.recurring_pattern : null,
      company_id: formData.company_id, // 管理ユーザーが選択した企業ID
    };

    // 再試行用に保存
    setLastPayload(saveData);

    // onSaveを呼び出して結果を取得
    const result = await onSave(saveData);

    // サーバー側でエラーが発生した場合
    if (result && !result.success && result.error) {
      setServerError(result.error);
    }
  };

  const handleRetry = async () => {
    if (!lastPayload || !onSave) return;
    setServerError(null);
    const result = await onSave(lastPayload);
    if (result && !result.success && result.error) {
      setServerError(result.error);
    }
  };

  /**
   * 削除処理
   */
  const handleDelete = () => {
    if (reservation && onDelete) {
      onDelete(reservation.id);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="modal-header">
          <h2 id="modal-title">{isReadOnly ? '予約の詳細' : (isEditing ? '予約を編集' : '新規予約')}</h2>
          <button className="modal-close" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* サーバーエラー表示 */}
            {serverError && (
              <div className="error-message" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '4px' }}>
                <div style={{ marginBottom: '8px' }}>{serverError}</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button type="button" onClick={handleRetry} className="btn-primary" style={{ padding: '6px 10px' }}>
                    再試行
                  </button>
                  <button type="button" onClick={() => setServerError(null)} className="btn-secondary" style={{ padding: '6px 10px' }}>
                    閉じる
                  </button>
                </div>
              </div>
            )}

            {/* 会議室選択 */}
            <div className="form-group">
              <label htmlFor="room-type-select">会議室 *</label>
              <select
                id="room-type-select"
                value={formData.room_type}
                onChange={(e) => handleChange('room_type', e.target.value)}
                className={errors.room_type ? 'error' : ''}
                disabled={isReadOnly}
                aria-invalid={errors.room_type ? 'true' : 'false'}
                aria-describedby={errors.room_type ? 'room-type-error' : undefined}
              >
                <option value="">選択してください</option>
                {ROOM_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {ROOMS[type].name}
                  </option>
                ))}
              </select>
              {errors.room_type && <span id="room-type-error" className="error-message" role="alert">{errors.room_type}</span>}
            </div>

            {/* 企業選択（システム管理者のみ表示） */}
            {isSystemAdmin() && (
              <div className="form-group">
                <label htmlFor="company-select">予約企業 *</label>
                <select
                  id="company-select"
                  value={formData.company_id || ''}
                  onChange={(e) => handleChange('company_id', parseInt(e.target.value))}
                  className={errors.company_id ? 'error' : ''}
                  disabled={companiesLoading || isReadOnly}
                  aria-invalid={errors.company_id ? 'true' : 'false'}
                  aria-describedby={errors.company_id ? 'company-error' : undefined}
                >
                  <option value="">選択してください</option>
                  {companies.map((company) => (
                    <option key={company.company_id} value={company.company_id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
                {errors.company_id && <span id="company-error" className="error-message" role="alert">{errors.company_id}</span>}
                {companiesLoading && <span className="loading-text" role="status" aria-live="polite">企業一覧を読み込み中...</span>}
              </div>
            )}

            {/* 作成者情報（既存予約の場合のみ表示） */}
            {isEditing && reservation?.creator_name && (
              <div className="form-group">
                <label>作成者</label>
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}>
                  {reservation.creator_name}
                  {reservation.creator_email && (
                    <span style={{ color: '#666', marginLeft: '8px' }}>
                      ({reservation.creator_email})
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* タイトル */}
            <div className="form-group">
              <label htmlFor="title-input">タイトル *</label>
              <input
                id="title-input"
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="会議のタイトル"
                maxLength={255}
                className={errors.title ? 'error' : ''}
                disabled={isReadOnly}
                aria-invalid={errors.title ? 'true' : 'false'}
                aria-describedby={errors.title ? 'title-error' : undefined}
              />
              {errors.title && <span id="title-error" className="error-message" role="alert">{errors.title}</span>}
            </div>

            {/* 日付・開始時間・終了時間（1行表示・Googleカレンダー風） */}
            <div className="form-group">
              <div className="datetime-inputs-wrapper" style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexWrap: 'nowrap'
              }}>

                {/* 日付入力 */}
                <input
                  type="date"
                  value={formatDate(formData.start_datetime)}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className={errors.start_datetime ? 'error' : ''}
                  disabled={isReadOnly}
                  style={{
                    width: '120px',
                    padding: '8px 12px',
                    border: '1px solid #dadce0',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />

                {/* 開始時間入力 */}
                <div ref={startTimeRef} style={{ position: 'relative', width: '100px' }}>
                  <input
                    type="time"
                    value={formatTime(formData.start_datetime)}
                    onChange={(e) => handleStartTimeInput(e.target.value)}
                    onBlur={handleStartTimeBlur}
                    className={`time-input ${errors.start_datetime ? 'error' : ''}`}
                    disabled={isReadOnly}
                    step="60"
                    style={{
                      width: '100px',
                      padding: '8px 12px',
                      paddingRight: '30px',
                      border: '1px solid #dadce0',
                      borderRadius: '4px',
                      fontSize: '14px',
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield'
                    }}
                    title="分を手動入力できます（確定時に15分単位に丸め）"
                  />
                  <div
                    style={{
                      position: 'absolute',
                      right: '4px',
                      top: 0,
                      height: '100%',
                      width: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'auto',
                      color: '#5f6368',
                      fontSize: '12px'
                    }}
                    role="button"
                    aria-label="時間を選択"
                    onClick={() => { if (!isReadOnly) setShowStartDropdown(!showStartDropdown); }}
                    className="time-dropdown-toggle"
                  >
                    ▼
                  </div>
                  {showStartDropdown && (
                    <div
                      className="dropdown-enter"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        zIndex: 20,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        background: '#fff',
                        border: '1px solid #dadce0',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                      }}
                    >
                      {timeOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={() => {
                            handleStartTimeChange(option.value);
                            setShowStartDropdown(false);
                          }}
                          style={{
                            padding: '6px 10px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* "から" テキスト */}
                <span style={{ color: '#5f6368', fontSize: '14px' }}>から</span>

                {/* 終了時間入力 */}
                <div ref={endTimeRef} style={{ position: 'relative', width: '100px' }}>
                  <input
                    type="time"
                    value={formatTime(formData.end_datetime)}
                    onChange={(e) => handleEndTimeInput(e.target.value)}
                    onBlur={handleEndTimeBlur}
                    className={`time-input ${errors.end_datetime ? 'error' : ''}`}
                    disabled={isReadOnly}
                    step="60"
                    style={{
                      width: '100px',
                      padding: '8px 12px',
                      paddingRight: '30px',
                      border: '1px solid #dadce0',
                      borderRadius: '4px',
                      fontSize: '14px',
                      WebkitAppearance: 'none',
                      MozAppearance: 'textfield'
                    }}
                    title="分を手動入力できます（確定時に15分単位に丸め）"
                  />
                  <div
                    style={{
                      position: 'absolute',
                      right: '4px',
                      top: 0,
                      height: '100%',
                      width: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'auto',
                      color: '#5f6368',
                      fontSize: '12px'
                    }}
                    role="button"
                    aria-label="時間を選択"
                    onClick={() => { if (!isReadOnly) setShowEndDropdown(!showEndDropdown); }}
                    className="time-dropdown-toggle"
                  >
                    ▼
                  </div>
                  {showEndDropdown && (
                    <div
                      className="dropdown-enter"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        zIndex: 20,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        background: '#fff',
                        border: '1px solid #dadce0',
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
                      }}
                    >
                      {timeOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={() => {
                            handleEndTimeChange(option.value);
                            setShowEndDropdown(false);
                          }}
                          style={{
                            padding: '6px 10px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {(errors.start_datetime || errors.end_datetime) && (
                <span className="error-message">
                  {errors.start_datetime || errors.end_datetime}
                </span>
              )}
            </div>

            {/* 繰り返し予約 */}
            {!isEditing && (
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_recurring}
                    onChange={(e) => {
                      handleChange('is_recurring', e.target.checked);
                      setShowRecurringOptions(e.target.checked);
                    }}
                  />
                  繰り返し予約
                </label>
              </div>
            )}

           {/* 繰り返しオプション */}
           {formData.is_recurring && (
               <RecurringPatternEditor
                   pattern={formData.recurring_pattern}
                   startDate={formData.start_datetime}
                   onChange={(pattern) => handleChange('recurring_pattern', pattern)}
               />
           )}

            {/* 備考 */}
            <div className="form-group">
              <label>備考</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="備考（任意）"
                maxLength={1023}
                rows="3"
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div className="modal-footer">
            {!isReadOnly && isEditing && onDelete && canDelete && (
              <button type="button" onClick={handleDelete} className="btn-danger">
                削除
              </button>
            )}
            <div className="button-group-right">
              <button type="button" onClick={onClose} className="btn-secondary">
                {isReadOnly ? '閉じる' : 'キャンセル'}
              </button>
              {!isReadOnly && (
                <button type="submit" className="btn-primary">
                  {isEditing ? '更新' : '作成'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReservationModal;
